import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CssBaseline,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import './styles.css';
import newPipelineImage from '../../assets/images/newPipeline.png';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import LeadStore from '../../store/LeadStore';
import AuthStore from '../../store/AuthStore';
import LoadingProgressStore from '../../store/LoadingProgressStore';
import { toJS } from 'mobx';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import pipelineVector from '../../assets/svg/pipelineVector.svg';

const drawerHeight = window.innerHeight;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  height: drawerHeight,
  flexShrink: 0,
}));

const StyledDrawerPaper = styled('div')(({ theme }) => ({
  height: drawerHeight,
  width: '100%',
  bottom: 0,
  overflow: 'auto',
}));

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const steps = ['Name your new pipeline', 'Name your first lead', 'Define your lead status'];

const leadPriorities = [
  { id: 1, name: 'Super High Priority' },
  { id: 2, name: 'High Priority' },
  { id: 3, name: 'Medium Priority' },
  { id: 4, name: 'Low Priority' },
];

export const NewLeadDrawer = observer(({ open, toggleDrawer }) => {
  const [completedStep, setCompletedStep] = useState(0);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [selectedLeadPriority, setSelectedLeadPriority] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [pipelines, setPipelines] = useState([]);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  useEffect(() => {
    open && LeadStore.getPipelines();
  }, [open]);

  useEffect(() => {
    setPipelines(toJS(LeadStore.pipelines));
    setActiveStep(1);
    LoadingProgressStore.toggleIsLoading(false);
  }, [LeadStore.pipelines]);

  useEffect(() => {
    open && !LeadStore.getPipelinesSuccess && LoadingProgressStore.toggleIsLoading(true);
  }, [open, LeadStore.getPipelinesSuccess]);

  const getStepsTemplate = () => {
    if (activeStep === 1) {
      return <Step1 pipelines={pipelines} handleStepComplete={handleStep1Complete}></Step1>;
    } else if (activeStep === 2) {
      return <Step2 selectedPipeline={selectedPipeline} handleStepComplete={handleStep2Complete}></Step2>;
    } else if (activeStep === 3) {
      return <Step3 leadPriorities={leadPriorities} handleStepComplete={handleStep3Complete}></Step3>;
    } else {
      return '';
    }
  };

  const handleStep1Complete = (pipeline) => {
    setSelectedPipeline(pipeline);
    setCompletedStep(1);
    setActiveStep(2);
  };

  const handleStep2Complete = (details) => {
    setLeadDetails(details);
    setCompletedStep(2);
    setActiveStep(3);
  };

  const handleStep3Complete = (priority) => {
    setSelectedLeadPriority({ ...priority });
  };

  useEffect(() => {
    if (selectedLeadPriority) {
      LoadingProgressStore.toggleIsLoading(true);
      LeadStore.createLead({
        ...leadDetails,
        pipeLinesId: selectedPipeline.id,
        priorityId: selectedLeadPriority.id,
        userId: AuthStore.userDetails.Id,
        assignedUserId: AuthStore.userDetails.Id,
        actiondDate: new Date(),
      });
    }
  }, [selectedLeadPriority]);

  useEffect(() => {
    if (LeadStore.newLeadSuccess) {
      setCompletedStep(3);
      handleCloseDrawer();
    }
  }, [LeadStore.newLeadSuccess]);

  const handleCloseDrawer = () => {
    toggleDrawer();
  };

  return (
    <StyledDrawer anchor="bottom" open={open} onClose={toggleDrawer}>
      <StyledDrawerPaper>
        <Card id="steps-card">
          <CardContent>
            <Stack direction={'column'} spacing={2}>
              <Stack direction={'row'} alignItems="center">
                <Grid item xs={4} sm={4} md={4} sx={{ width: '-webkit-fill-available' }}>
                  <Item className="app-full-logo"></Item>
                </Grid>
                <Grid item xs={8} sm={8} md={8} sx={{ marginLeft: 'auto' }}>
                  <Item sx={{ boxShadow: 'none' }}>
                    <IconButton aria-label="close" size="large" color="error" onClick={handleCloseDrawer}>
                      <HighlightOffIcon />
                    </IconButton>
                  </Item>
                </Grid>
              </Stack>
              <Stack direction={'column'} spacing={3}>
                <Stack
                  direction={'row'}
                  justifyContent={'center'}
                  id="start-pipeline-stepper"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={{ xs: 3, sm: 3, md: 2, lg: 0 }}
                >
                  <Typography
                    component="p"
                    sx={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#A85400',
                    }}
                  >
                    Set up your sales process
                  </Typography>
                  <Stepper sx={{ flexGrow: 1 }} activeStep={completedStep} alternativeLabel>
                    {steps.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Stack>
                <Stack direction={'row'} alignItems="stretch">
                  <CssBaseline />
                  <Grid id="steps-container" container spacing={3} justifyContent={'space-between'}>
                    <Grid item xs={12} sm={6} md={6} alignItems="flex-start" justifyContent="flex-start">
                      <Item>
                        <Stack direction={'column'} spacing={3}>
                          <Stack>
                            <Typography component="p">Step {activeStep} of 3</Typography>
                          </Stack>
                          {open ? getStepsTemplate() : ''}
                        </Stack>
                      </Item>
                    </Grid>
                    {!isSmallScreen && (
                      <React.Fragment>
                        <Grid item xs={12} sm={6} md={6} alignSelf="center">
                          <Stack justifyContent={'space-around'}>
                            <img className="new-pipeline-image" src={newPipelineImage} alt="new-pipeline"></img>
                          </Stack>
                        </Grid>
                      </React.Fragment>
                    )}
                  </Grid>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </StyledDrawerPaper>
    </StyledDrawer>
  );
});
